import {isLoggedIn} from '@github-ui/client-env'
import type {RepositoryDataResponse} from '@github-ui/data-router-error-helpers'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {createQueryClient, getQueryClient} from '@github-ui/query-client'
import {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {mainQuery, makeSelectDataFromQueryData} from '@github-ui/react-core/future/main-query'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {
  createServerPersistedQueryPersister,
  persistQueryData,
  removePersistedQueryData,
} from '@github-ui/react-query-persister'
import {matchPath} from '@github-ui/react-router'
import {DEFAULT_RELAY_CACHE_TTL} from '@github-ui/relay-query-cache/helpers'
import {IssueViewerPage} from '@github-ui/server-persisted-queries'
import type {IssueViewerPageQuery} from '@github-ui/server-persisted-queries/types/IssueViewerPage'
import {IS_SERVER} from '@github-ui/ssr-utils'
import {getIssueViewerStorageQueryKey, ISSUE_VIEWER_PERSISTER_PREFIX} from './issue-viewer-storage-key'

import {runSharedPreheat} from './shared-preheat-run'

export type IssueViewerPayload = RepositoryDataResponse<IssueViewerPageQuery>

export type IssueViewerQueryData = {
  meta?: {title?: string}
  payload: IssueViewerPayload
}

export type IssueCoordinates = {owner: string; repo: string; number: number}
export type IssueViewerQueryIdentifier = IssueCoordinates | {permalink: string}
export type IssueViewerDocumentMode = 'canonical' | 'highlighted-event' | 'timeline-page'

const preheatTimestamps = new WeakMap<object, number>()
let preheatQueryClient: ReturnType<typeof createQueryClient> | undefined

export function buildIssueViewerRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  const query = mainQuery<IssueViewerPayload>({
    queryDeps: ({pathname, searchParams, hash}) => ({
      pathname,
      searchParams: {queryId: IssueViewerPage},
      documentMode: issueViewerDocumentMode({searchParams, hash: hash ?? ''}),
    }),
    persister: createServerPersistedQueryPersister({
      queryHash: IssueViewerPage,
      storageQueryKey: queryKey => {
        const queryDeps = queryKey[4]
        if (!isRecord(queryDeps) || typeof queryDeps.pathname !== 'string') return

        const match = matchPath('/:owner/:repo/issues/:number', queryDeps.pathname)
        const {owner, repo, number} = match?.params ?? {}
        if (!owner || !repo || !number || !/^\d+$/.test(number)) return

        const issueNumber = Number(number)
        if (!Number.isSafeInteger(issueNumber) || issueNumber <= 0) return

        try {
          return getIssueViewerStorageQueryKey({
            number: issueNumber,
            owner: decodeURIComponent(owner),
            repo: decodeURIComponent(repo),
          })
        } catch {
          return
        }
      },
      maxAgeMs: DEFAULT_RELAY_CACHE_TTL,
      prefix: ISSUE_VIEWER_PERSISTER_PREFIX,
      // An IndexedDB snapshot may be stale even within its TTL, so revalidate after restoring it.
      alwaysRefetch: true,
      restoreOnPreload: true,
      // SSR uses setQueryData rather than a fetch; explicitly persist that initial response.
      persistInitialData: true,
      getDataUpdatedAt: data => (isRecord(data) ? preheatTimestamps.get(data) : undefined),
      dataValidator: {name: 'issue-viewer-page', isValid: isCacheableIssueViewerQueryData},
      getPersistenceMode: persistedQuery =>
        issueViewerDocumentModeFromQueryKey(persistedQuery.queryKey) === 'canonical' ? 'read-write' : 'disabled',
      sendAnalyticsEvent: (eventType, target, payload) => sendEvent(eventType, {...payload, target}),
    }),
  })

  return appBuilder.createQueryRouteConfig('issueViewerRoute', {
    path: '/:owner/:repo/issues/:number',
    queries: [
      {
        ...query,
        select: makeSelectDataFromQueryData<IssueViewerPayload>(),
        // eslint-disable-next-line @github-ui/github-monorepo/require-query-schema
        queryFn: (queryKey: Parameters<typeof query.queryFn>[0], options: Parameters<typeof query.queryFn>[1]) =>
          options.meta?.isPreload === true
            ? fetchPrimaryPreheat(queryKey.queryDeps.pathname, options)
            : query.queryFn(queryKey, options),
      },
    ],
  })
}

let preheatRoute: ReturnType<typeof buildIssueViewerRoute> | undefined

function getPreheatRoute() {
  return (preheatRoute ??= buildIssueViewerRoute(
    // eslint-disable-next-line @github-ui/github-monorepo/react-app-name -- Builds query options for the existing issues app; does not register an app.
    DataRouterApplicationBuilder.create('issues', {
      queryKeyAppName: () => 'repo',
    }),
  ))
}

export function issueViewerPrimaryQueryKey(identifier: IssueViewerQueryIdentifier) {
  const route = getPreheatRoute()
  const pathname =
    'permalink' in identifier
      ? new URL(identifier.permalink, 'https://github.com').pathname
      : route.generatePath({
          owner: encodeURIComponent(identifier.owner),
          repo: encodeURIComponent(identifier.repo),
          number: String(identifier.number),
        })
  return route.queries.mainQuery.makeQueryKey({
    pathname,
    searchParams: {queryId: IssueViewerPage},
    documentMode: 'canonical',
  })
}

export function issueViewerPrimaryQueryOptions(identifier: IssueViewerQueryIdentifier) {
  const route = getPreheatRoute()
  return route.buildQueryOptions('mainQuery', issueViewerPrimaryQueryKey(identifier))
}

export function issueViewerDocumentMode({
  searchParams,
  hash,
}: {
  searchParams: URLSearchParams
  hash: string
}): IssueViewerDocumentMode {
  const timelinePage = Number(searchParams.get('timeline_page'))
  if (Number.isSafeInteger(timelinePage) && timelinePage > 0) return 'timeline-page'
  if (/^#(?:issuecomment|event)-[^-]+$/.test(hash)) return 'highlighted-event'
  return 'canonical'
}

export async function persistIssueViewerPrimary(
  identifier: IssueViewerQueryIdentifier,
  queryClient: ReturnType<typeof getQueryClient> = getQueryClient(),
): Promise<void> {
  const options = issueViewerPrimaryQueryOptions(identifier)
  await persistQueryData(options.persister, options.queryKey, queryClient)
}

export async function removePersistedIssueViewerPrimary(
  identifier: IssueViewerQueryIdentifier,
  queryClient: ReturnType<typeof getQueryClient> = getQueryClient(),
): Promise<void> {
  const options = issueViewerPrimaryQueryOptions(identifier)
  await removePersistedQueryData(options.persister, options.queryKey, queryClient)
}

export async function preheatIssueViewerPrimary({
  owner,
  repo,
  number,
  source,
  signal,
  onError,
}: IssueCoordinates & {source: string; signal?: AbortSignal; onError?: (error: unknown) => void}): Promise<void> {
  if (!canPreheat() || signal?.aborted || !Number.isSafeInteger(number) || number <= 0) return

  const queryClient = getQueryClient()
  const queryOptions = issueViewerPrimaryQueryOptions({owner, repo, number})
  const {queryKey} = queryOptions
  const existingQuery = queryClient.getQueryCache().find({queryKey, exact: true})
  if (existingQuery && (existingQuery.getObserversCount() > 0 || existingQuery.state.fetchStatus !== 'idle')) return
  const state = existingQuery?.state
  if (state?.status === 'success' && !state.isInvalidated && isCacheableIssueViewerQueryData(state.data)) return

  // Like legacy preheating, only write the persisted record. A private client keeps the pending
  // preheat out of the shared cache, so viewers never join it; they restore the record and refresh.
  const preheatClient = (preheatQueryClient ??= createQueryClient())
  const meta = {isPreload: true, preloadedAt: Date.now(), preheatSource: source}
  await runSharedPreheat({
    client: preheatClient,
    queryKey,
    signal,
    onError,
    fetch: () => preheatClient.fetchQuery({...queryOptions, gcTime: 0, meta}),
  })
}

async function fetchPrimaryPreheat(
  pathname: string,
  options: {signal: AbortSignal; meta: Record<string, unknown> | undefined},
): Promise<IssueViewerQueryData> {
  if (!canPreheat()) throw new Error('Issue preheating is disabled')

  const match = matchPath('/:owner/:repo/issues/:number', pathname)
  const {owner: encodedOwner, repo: encodedRepo, number} = match?.params ?? {}
  const owner = encodedOwner && decodeURIComponent(encodedOwner)
  const repo = encodedRepo && decodeURIComponent(encodedRepo)
  const issueNumber = Number(number)
  if (!owner || !repo || !number || !/^\d+$/.test(number) || !Number.isSafeInteger(issueNumber) || issueNumber <= 0) {
    throw new Error('Invalid issue preheat coordinates')
  }

  const source = typeof options.meta?.preheatSource === 'string' ? options.meta.preheatSource : 'data-router'
  const response = await queryFnFetch<unknown>({
    queryDeps: {
      pathname: `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/preheat`,
      searchParams: {number, source, query: 'IssueViewerPage', version: '1', queryId: IssueViewerPage},
      init: {signal: options.signal, headers: {'X-GITHUB-PREHEAT': source}},
    },
  })

  if (
    !isRecord(response) ||
    response.version !== 1 ||
    response.queryHash !== IssueViewerPage ||
    typeof response.dataUpdatedAt !== 'number' ||
    !Number.isSafeInteger(response.dataUpdatedAt) ||
    response.dataUpdatedAt <= 0 ||
    response.dataUpdatedAt > Date.now() + 60_000 ||
    Date.now() - response.dataUpdatedAt > DEFAULT_RELAY_CACHE_TTL ||
    !isCacheableIssueViewerQueryData(response.queryData)
  ) {
    throw new Error('Unsupported issue preheat response')
  }

  const issue = response.queryData.payload.data?.repository?.issue
  if (
    !issue ||
    issue.number !== issueNumber ||
    issue.repository.owner.login.toLowerCase() !== owner.toLowerCase() ||
    issue.repository.name.toLowerCase() !== repo.toLowerCase()
  ) {
    throw new Error('Issue preheat response does not match the requested issue')
  }

  preheatTimestamps.set(response.queryData, response.dataUpdatedAt)
  return response.queryData
}

function canPreheat(): boolean {
  return !IS_SERVER && isLoggedIn() && !isFeatureEnabled('disable_preheating_for_issues')
}

function issueViewerDocumentModeFromQueryKey(queryKey: readonly unknown[]): IssueViewerDocumentMode {
  const queryDeps = queryKey[4]
  if (!isRecord(queryDeps)) return 'canonical'
  const documentMode = queryDeps.documentMode
  return documentMode === 'highlighted-event' || documentMode === 'timeline-page' ? documentMode : 'canonical'
}

function isCacheableIssueViewerQueryData(value: unknown): value is IssueViewerQueryData {
  if (!isRecord(value) || !isRecord(value.payload)) return false

  const {meta, payload} = value
  if (meta !== undefined && (!isRecord(meta) || (meta.title !== undefined && typeof meta.title !== 'string'))) {
    return false
  }
  if (payload.errors != null && (!Array.isArray(payload.errors) || payload.errors.length > 0)) return false

  const data = payload.data
  if (!isRecord(data) || !isRecord(data.repository) || !isRecord(data.repository.issue)) return false

  return typeof data.repository.issue.id === 'string' && data.repository.issue.id.length > 0
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
